/**
 * MachineSchedulePage.tsx
 * Top-level orchestration for the Machine Schedule view.
 *
 * Phase 2 shipped this as a day-granularity grid with right-edge drag-resize.
 * The Machine Schedule UX overhaul layers on top:
 *   - M1: seamless edits — optimistic local updates + quiet conflict refresh
 *         (no more grid-unmount/spinner-flash after a drag) and undo/redo.
 *
 * Data + mutations live in hooks so this file stays an orchestration shell:
 *   - useScheduleData      — bookings/machines/conflicts fetch + optimistic mutators
 *   - useScheduleMutations — commit funnel (optimistic → persist → conflicts → undo)
 *   - useMachineScheduleFilters — URL-sticky filters
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  Snackbar,
  LinearProgress,
  Button,
} from '@mui/material';
import { MachineScheduleToolbar } from './MachineScheduleToolbar';
import { MachineScheduleGrid } from './MachineScheduleGrid';
import { MachineScheduleLegend } from './MachineScheduleLegend';
import { AddEventDialog } from './AddEventDialog';
import { SchedulingConflictsPanel } from './SchedulingConflictsPanel';
import { BookingDetailDrawer } from '../../../components/schedule/BookingDetailDrawer';
import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../components/shared/ConfirmDialog';
import type { ConflictType } from '../../../components/schedule/ConflictBadge';
import {
  useMachineScheduleFilters,
  applyMachineScheduleFilters,
  defaultWindow,
} from './useMachineScheduleFilters';
import { useScheduleData, type BookingPlacementPatch } from '../../../hooks/useScheduleData';
import { useScheduleMutations, type ScheduleToast } from '../../../hooks/useScheduleMutations';
import { useScheduleAutoRefresh } from '../../../hooks/useScheduleAutoRefresh';
import { onScheduleChange, emitScheduleChange } from '../../../services/scheduleBus';
import { bookingMatchesQuery, machineMatchesQuery } from '../../../utils/scheduleSearch';
import { bookingPrimaryLabel } from '../../../utils/bookingLabel';
import { suggestResolution } from '../../../utils/scheduleResolve';
import { useScheduleHolidays } from '../../../hooks/useScheduleHolidays';
import { buildDayRange, COMPACT_ROW_HEIGHT, ROW_HEIGHT } from './scheduleGridShared';
import { useScheduleGridDisplay } from '../../../hooks/useScheduleGridDisplay';
import { isIndexMachineCode } from '../../../utils/indexMachine';
import {
  mergePrepBookings,
  expandPrepPatch,
  isMergedPrepBooking,
  weekdayOnly,
} from '../../../utils/prepBookingMerge';
import type { LabSection, MachineBookingCalendar } from '../../../types/schedule';

// ── density (zoom) ──────────────────────────────────────────────────────────
type Density = 'compact' | 'normal' | 'wide';
const DENSITY_PX: Record<Density, number> = { compact: 24, normal: 38, wide: 64 };
const DENSITY_KEY = 'moftaki.machineSchedule.density';
function loadDensity(): Density {
  try {
    const v = localStorage.getItem(DENSITY_KEY);
    return v === 'compact' || v === 'wide' ? v : 'normal';
  } catch {
    return 'normal';
  }
}
function bookingLabel(b: MachineBookingCalendar): string {
  return bookingPrimaryLabel(b);
}

// ── date-window helpers ────────────────────────────────────────────────────

/** Add `days` to an ISO date string. */
function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inclusive day-count of a [start, end] window (1 = single day). */
function windowLengthDays(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00`).getTime();
  const b = new Date(`${end}T00:00:00`).getTime();
  return Math.round((b - a) / (24 * 60 * 60 * 1000)) + 1;
}

// REQ-04: density auto-compacts for wide windows so a month-ish range stays
// legible without the user reaching for the density toggle.
const WIDE_WINDOW_THRESHOLD = 21;

// ── component ──────────────────────────────────────────────────────────────

export const MachineSchedulePage: React.FC = () => {
  // URL-sticky filters (+ focus deep-link hint) — REQ-04: also owns the
  // visible date window (start/end), so the window survives navigation/refresh.
  const {
    filterState,
    setFilter,
    clearFilters,
    hasActiveFilters,
    focusBookingKey,
    window: dateWindow,
    setWindow,
  } = useMachineScheduleFilters();

  const startDate = dateWindow.start;
  const endDate = dateWindow.end;

  // REQ-04: pending picker values — edited locally, committed to the URL
  // window on "Apply" (mirrors MasterGantt's Start/End + Apply pattern).
  const [pendingStart, setPendingStart] = useState<string>(startDate);
  const [pendingEnd, setPendingEnd] = useState<string>(endDate);

  // Keep the pickers in sync when the window changes from elsewhere
  // (Today / Prev / Next / back-forward navigation).
  useEffect(() => {
    setPendingStart(startDate);
    setPendingEnd(endDate);
  }, [startDate, endDate]);

  // Data layer (fetch + optimistic mutators).
  const {
    bookings,
    machines,
    conflicts,
    capabilityByTaskType,
    loading,
    refreshing,
    error,
    setError,
    reload,
    reloadConflicts,
    applyLocalBookingChange,
    removeLocalBooking,
    restoreLocalBooking,
  } = useScheduleData(startDate, endDate);

  // Snackbars: action result (with Undo) + action error.
  const [toast, setToast] = useState<ScheduleToast | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Add Event dialog (Phase 2 item 6).
  const [addEventOpen, setAddEventOpen] = useState(false);

  // True while a drag gesture is active (suppresses keyboard undo / future auto-refresh).
  const [dragActive, setDragActive] = useState(false);

  // Capacity heatmap toggle (M6).
  const [showCapacity, setShowCapacity] = useState(false);

  // Density / zoom (M7) — persisted column width.
  const [density, setDensity] = useState<Density>(loadDensity);
  const changeDensity = useCallback((d: Density) => {
    setDensity(d);
    try { localStorage.setItem(DENSITY_KEY, d); } catch { /* ignore */ }
  }, []);
  // REQ-04: a wide window (≥ ~3 weeks) has many columns, so auto-compact the
  // column width to keep it legible — without overwriting the user's stored
  // density preference (it returns to their choice when the window narrows).
  const isWideWindow = windowLengthDays(startDate, endDate) >= WIDE_WINDOW_THRESHOLD;
  const effectiveDensity: Density = isWideWindow ? 'compact' : density;
  const dayColWidth = DENSITY_PX[effectiveDensity];

  // Compact one-line machine rows (sticky-headers UX pass) — shared pref with
  // the Scenario Planning grid (localStorage-backed).
  const { compactRows, setCompactRows } = useScheduleGridDisplay();

  // Drag-on-empty-cells → prefill the Add Event dialog (M7).
  const [createEvent, setCreateEvent] = useState<{ machineKey: string; start: string; end: string } | null>(null);

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  // Mutation funnel (optimistic commit + persist + conflicts + undo/redo).
  const { commitPlacement, commitPlacementBatch, commitDelete, isMutating } = useScheduleMutations({
    applyLocalBookingChange,
    removeLocalBooking,
    restoreLocalBooking,
    reloadConflicts,
    onToast: setToast,
    onError: setActionError,
    dragActive,
  });

  // Detail drawer + delete confirmation.
  const [selectedBookingKey, setSelectedBookingKey] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // RM-23: stat holidays for the auto-resolve suggestion (working-day starts).
  const { holidays: windowHolidays } = useScheduleHolidays(startDate, endDate);
  const holidayDates = useMemo(
    () => windowHolidays.map((h) => h.date.slice(0, 10)),
    [windowHolidays],
  );

  // BUG-2026-0193: the grid folds a source's per-line prep bookings on one
  // machine into a single bar. The drawer + keyboard nudge must see the SAME
  // rows the user clicks, so resolve selections against the folded list (same
  // holiday-aware contiguity rule the grid uses).
  const displayBookings = useMemo(() => {
    const holidaySet = new Set(holidayDates);
    return mergePrepBookings(bookings, (iso) => weekdayOnly(iso) && !holidaySet.has(iso));
  }, [bookings, holidayDates]);

  // M8: background live-refresh — paused during a drag, an open drawer, or a
  // pending mutation so it never clobbers an in-progress optimistic edit.
  const { lastSyncedLabel } = useScheduleAutoRefresh({
    reload,
    paused: dragActive || isMutating || selectedBookingKey !== null,
  });

  // Cross-view live refresh: an assignment/booking change made elsewhere (Employee
  // Schedule, Scenario Planning) quietly revalidates this grid — no remount, no flash.
  // A self-echo from this page's own edits just re-confirms the optimistic state.
  const reloadRef = useRef(reload);
  useEffect(() => { reloadRef.current = reload; }, [reload]);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = onScheduleChange(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void reloadRef.current({ quiet: true }); }, 300);
    });
    return () => { if (timer) clearTimeout(timer); off(); };
  }, []);

  // ── toolbar handlers (REQ-04: free start/end date pickers) ───────────────
  // Today: reset to the default window ("Next 3 months" preset — BUG-2026-0195).
  const handleToday = useCallback(() => setWindow(defaultWindow()), [setWindow]);

  // Prev/Next: shift the window by its own length (keeps the chosen width).
  const handlePrev = useCallback(() => {
    const len = windowLengthDays(startDate, endDate);
    setWindow({ start: addDays(startDate, -len), end: addDays(endDate, -len) });
  }, [startDate, endDate, setWindow]);
  const handleNext = useCallback(() => {
    const len = windowLengthDays(startDate, endDate);
    setWindow({ start: addDays(startDate, len), end: addDays(endDate, len) });
  }, [startDate, endDate, setWindow]);

  // Apply: commit the pending picker values to the URL window. Guard against an
  // end-before-start by swapping (parseWindowParams also enforces this on read).
  const handleApplyWindow = useCallback(() => {
    let start = pendingStart;
    let end = pendingEnd;
    if (!start || !end) return;
    if (end < start) [start, end] = [end, start];
    setWindow({ start, end });
  }, [pendingStart, pendingEnd, setWindow]);

  // Quick-range preset: commit the preset window directly. setWindow is URL-
  // backed, so the data hook refetches and the pending pickers re-sync via the
  // startDate/endDate effect — no stale-closure dance needed here.
  const handleRangePreset = useCallback(
    (start: string, end: string) => setWindow({ start, end }),
    [setWindow],
  );

  // ── filters + comprehensive search ────────────────────────────────────────
  const q = filterState.q.trim();
  const visibleBookings = (() => {
    const filtered = applyMachineScheduleFilters(bookings, filterState);
    return q ? filtered.filter((b) => bookingMatchesQuery(b, q)) : filtered;
  })();

  // Machines that still have a matching booking (so their rows survive the search).
  const machineKeysWithVisibleBooking = new Set(visibleBookings.map((b) => b.MachineKey));

  const visibleMachines = machines.filter((m) => {
    if (filterState.labSections.length > 0) {
      const section = m.labSection ?? 'Other';
      if (!filterState.labSections.includes(section as LabSection)) return false;
    }
    if (filterState.machineCodes.length > 0) {
      if (!filterState.machineCodes.includes(m.code)) return false;
    }
    // Search: keep a row if the machine itself matches, or it has a matching booking.
    if (q && !machineMatchesQuery(m, q) && !machineKeysWithVisibleBooking.has(m.machineKey)) {
      return false;
    }
    return true;
  });

  // Filtered-empty: machines exist but a filter/search hid them all. Without this
  // the grid falls back to its generic "No machines — add some" message, which
  // misleads when the cause is a filter (UX audit 2026-06-20). hasActiveFilters
  // already covers a non-empty search (isFilterDefault checks q.trim()), and
  // clearFilters() also deletes the search param — so one handler clears both.
  const filteredEmpty =
    !loading && machines.length > 0 && visibleMachines.length === 0 && hasActiveFilters;

  // Item 6.1 (A4) — the virtual pooled Index machine never gets real bookings
  // (its capacity currency is tests/day, tracked via IndexAllocation, not
  // MachineBooking), so it must never be offered as a manual Maintenance /
  // Calibration / Out of Service event target.
  const machinesForManualEvents = machines.filter((m) => !isIndexMachineCode(m.code));

  // ── drag commit (resize both edges / move / cross-machine) ────────────────
  // The grid-level drag engine hands us the booking + a placement patch; the
  // commit funnel applies it optimistically, persists, refreshes conflicts, and
  // registers undo — all without unmounting the grid.
  // BUG-2026-0193: a merged prep bar fans out to every member (same day delta,
  // same target machine) as ONE undoable edit; a plain bar is a one-item batch.
  const handleCommitPlacement = useCallback(
    (booking: MachineBookingCalendar, patch: BookingPlacementPatch, label: string) => {
      const items = expandPrepPatch(booking, patch).map((p) => ({ key: p.booking.MachineBookingKey, patch: p.patch }));
      void commitPlacementBatch(items, label);
    },
    [commitPlacementBatch],
  );

  // Single-click on a bar → open the detail drawer.
  const handleBookingClick = useCallback((booking: MachineBookingCalendar) => {
    setSelectedBookingKey(booking.MachineBookingKey);
  }, []);

  // Drag across empty cells → open Add Event prefilled with machine + range.
  const handleCreateAt = useCallback((machineKey: string, start: string, end: string) => {
    setCreateEvent({ machineKey, start, end });
  }, []);

  // Delete → confirm, then optimistic remove.
  const handleRequestDelete = useCallback(
    (booking: MachineBookingCalendar) => {
      const name = booking.TaskName ?? booking.EventType ?? 'this booking';
      // BUG-2026-0193: deleting a merged prep bar removes every member booking.
      const targets = isMergedPrepBooking(booking) ? booking.mergedPrepBookings : [booking];
      const what = targets.length > 1 ? `all ${targets.length} prep bookings behind “${name}”` : `“${name}”`;
      setConfirm({
        open: true,
        title: 'Delete booking?',
        message: `Remove ${what} from ${booking.MachineCode}? This can’t be undone.`,
        confirmLabel: 'Delete',
        confirmColor: 'error',
        onConfirm: () => {
          setSelectedBookingKey(null);
          void (async () => {
            for (const t of targets) await commitDelete(t, `Deleted ${name}`);
          })();
        },
      });
    },
    [commitDelete],
  );

  // Derive the live selected booking + its capability set + conflict state.
  // (Folded list — a click on a merged prep bar selects the merged row.)
  const selectedBooking = selectedBookingKey
    ? displayBookings.find((b) => b.MachineBookingKey === selectedBookingKey) ?? null
    : null;

  const selectedCapableKeys: Set<string> | null = (() => {
    if (!selectedBooking?.TaskTypeKey) return null;
    const set = capabilityByTaskType.get(selectedBooking.TaskTypeKey);
    return set && set.size > 0 ? set : null;
  })();

  const selectedConflictType: ConflictType | null = (() => {
    if (!selectedBooking || !conflicts) return null;
    const key = selectedBooking.MachineBookingKey;
    if (conflicts.hard.some((h) => h.bookings.some((b) => b.key === key))) return 'hard';
    if (conflicts.soft.some((s) => s.bookingKey === key)) return 'soft';
    return null;
  })();

  // M7: nearest-free-slot / capable-machine suggestion for a hard conflict.
  // RM-23: holiday-aware + run-mode aware — candidate starts are working days
  // only, and a continuous booking's scan sees neighbours' teardown padding.
  const machineCodeByKey = new Map(machines.map((m) => [m.machineKey, m.code]));
  const conflictSuggestion =
    selectedBooking && selectedConflictType === 'hard'
      ? suggestResolution(
          selectedBooking,
          bookings,
          selectedCapableKeys,
          (k) => machineCodeByKey.get(k) ?? k,
          buildDayRange(startDate, endDate),
          holidayDates,
        )
      : null;

  // ── jump-to-booking (deep link + conflicts panel) ─────────────────────────
  // Smooth-scrolls the booking bar to the CENTRE of the grid, then plays a
  // 1.8 s attention flash (a glowing ring that pulses twice) so the user's eye
  // lands on it. Robust to two things that previously made the deep link a
  // silent no-op (BUG report): (1) the bar not being painted yet right after
  // load — we retry briefly; (2) a UUID-casing mismatch between the URL param
  // and the DOM attribute — we match case-insensitively.
  const handleJumpToBooking = useCallback((bookingKey: string) => {
    const want = bookingKey.toLowerCase();

    let attempts = 0;
    const tryFocus = () => {
      // Query the live DOCUMENT on every attempt — NOT a captured container.
      // The grid Box is conditionally mounted ({!loading && <Box ref>}) and
      // remounts across load/refresh phases, so a captured node can be null or
      // detached (its querySelectorAll then finds nothing and the retry exhausts
      // silently — the original deep-link no-op). scrollIntoView scrolls the
      // correct ancestor/window regardless of which element we matched.
      const el =
        Array.from(
          document.querySelectorAll<HTMLElement>('[data-booking-key]'),
        ).find((n) => n.getAttribute('data-booking-key')?.toLowerCase() === want) ?? null;

      if (!el) {
        // Bar not painted yet (grid still loading) — retry for ~5 s, then give
        // up (the booking may be outside the current date range / filters).
        if (attempts++ < 30) window.setTimeout(tryFocus, 160);
        return;
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      // Web Animations API: pulses a yellow ring twice over 1.8 s and reverts
      // cleanly (no residual inline styles). Yellow matches the Employee
      // Schedule deep-link flash (scheduleRowFlash) for cross-view consistency.
      el.animate(
        [
          { boxShadow: '0 0 0 0 rgba(255, 213, 0, 0)', offset: 0 },
          { boxShadow: '0 0 0 6px rgba(255, 213, 0, 0.7)', offset: 0.2 },
          { boxShadow: '0 0 0 0 rgba(255, 213, 0, 0)', offset: 0.45 },
          { boxShadow: '0 0 0 6px rgba(255, 213, 0, 0.7)', offset: 0.7 },
          { boxShadow: '0 0 0 0 rgba(255, 213, 0, 0)', offset: 1 },
        ],
        { duration: 1800, easing: 'ease-in-out' },
      );
    };

    // Kick off via setTimeout, NOT requestAnimationFrame: rAF is paused while
    // the tab is hidden, so a deep link opened in a background tab would never
    // start scrolling. setTimeout still fires (throttled) in the background, and
    // the retry loop also uses setTimeout, so the whole flow is visibility-proof.
    window.setTimeout(tryFocus, 0);
  }, []);

  // ── Phase 4 item 4: auto-jump to a focused booking after load ─────────────
  // Fires once per distinct focusBookingKey (a ref guard) so the 60 s
  // auto-refresh doesn't re-scroll/re-flash. Triggered by the param being
  // present — NOT by a `loading` edge — and handleJumpToBooking polls the DOM
  // until the bar paints, so it's immune to where in the load cycle it runs.
  const focusedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusBookingKey) return;
    if (focusedKeyRef.current === focusBookingKey) return;
    focusedKeyRef.current = focusBookingKey;
    handleJumpToBooking(focusBookingKey);
  }, [focusBookingKey, handleJumpToBooking]);

  // ── M7: keyboard nudge & select ───────────────────────────────────────────
  // Arrows move the selected booking a day (Shift = resize end, Alt = resize
  // start); Up/Down hop to the prev/next capable machine; Esc deselects.
  useEffect(() => {
    if (!selectedBookingKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (dragActive) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        return;
      }
      // BUG-2026-0193: resolve against the folded list so a merged prep bar
      // nudges every member (via handleCommitPlacement's fan-out); a merged
      // bar has no meaningful edge, so Shift/Alt resize falls back to a move.
      const b = displayBookings.find((x) => x.MachineBookingKey === selectedBookingKey);
      if (!b) return;
      const merged = isMergedPrepBooking(b);

      if (e.key === 'Escape') {
        setSelectedBookingKey(null);
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const delta = e.key === 'ArrowRight' ? 1 : -1;
        if (e.shiftKey && !merged) {
          const newEnd = addDays(b.EndDate, delta);
          if (newEnd >= b.StartDate) void commitPlacement(b.MachineBookingKey, { EndDate: newEnd }, `Resized ${bookingLabel(b)}`);
        } else if (e.altKey && !merged) {
          const newStart = addDays(b.StartDate, delta);
          if (newStart <= b.EndDate) void commitPlacement(b.MachineBookingKey, { StartDate: newStart }, `Resized ${bookingLabel(b)}`);
        } else {
          handleCommitPlacement(
            b,
            { StartDate: addDays(b.StartDate, delta), EndDate: addDays(b.EndDate, delta) },
            `Moved ${bookingLabel(b)}`,
          );
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const capable = b.TaskTypeKey ? capabilityByTaskType.get(b.TaskTypeKey) ?? null : null;
        const order = [...visibleMachines].sort(
          (m1, m2) =>
            (m1.labSection ?? 'Other').localeCompare(m2.labSection ?? 'Other') ||
            m1.code.localeCompare(m2.code),
        );
        const curIdx = order.findIndex((m) => m.machineKey === b.MachineKey);
        if (curIdx < 0) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        for (let i = curIdx + step; i >= 0 && i < order.length; i += step) {
          const cand = order[i];
          if (!capable || capable.size === 0 || capable.has(cand.machineKey)) {
            handleCommitPlacement(b, { MachineKey: cand.machineKey }, `Moved ${bookingLabel(b)} → ${cand.code}`);
            break;
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBookingKey, displayBookings, visibleMachines, capabilityByTaskType, commitPlacement, handleCommitPlacement, dragActive]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* Page header */}
      <Box sx={{ pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          Machine Schedule
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View machine availability and scheduled events.
        </Typography>
      </Box>

      {/* Sticky toolbar */}
      <MachineScheduleToolbar
        startDate={startDate}
        endDate={endDate}
        pendingStart={pendingStart}
        pendingEnd={pendingEnd}
        onPendingStartChange={setPendingStart}
        onPendingEndChange={setPendingEnd}
        onApplyWindow={handleApplyWindow}
        onRangePreset={handleRangePreset}
        onToday={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        onAddEvent={() => setAddEventOpen(true)}
        filterState={filterState}
        machines={machines}
        hasActiveFilters={hasActiveFilters}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        showCapacity={showCapacity}
        onToggleCapacity={() => setShowCapacity((v) => !v)}
        density={effectiveDensity}
        onDensityChange={changeDensity}
        compactRows={compactRows}
        onToggleCompactRows={() => setCompactRows(!compactRows)}
      />

      {/* Error banner (load errors) */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Initial (blocking) load */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Loading schedule…
          </Typography>
        </Box>
      )}

      {/* Conflicts panel */}
      {!loading && conflicts && (
        <SchedulingConflictsPanel conflicts={conflicts} onJumpToBooking={handleJumpToBooking} />
      )}

      {/* Filtered-empty state — machines exist but the filter/search hid them all */}
      {filteredEmpty && (
        <Alert
          severity="info"
          sx={{ mt: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={clearFilters}>
              Clear {filterState.q ? 'filters & search' : 'filters'}
            </Button>
          }
        >
          No machines or bookings match your {filterState.q ? 'search and filters' : 'filters'}.
        </Alert>
      )}

      {/* Main grid — stays mounted across refreshes; a thin bar shows background activity */}
      {!loading && !filteredEmpty && (
        <Box
          ref={gridContainerRef}
          // FEAT-2026-0013 — bound this container and delegate ALL scrolling to
          // the grid inside it (display:flex + overflow:hidden + minHeight:0),
          // so the grid's sticky day-header freezes on vertical scroll instead
          // of this box scrolling the whole grid (header included) out of view.
          sx={{
            flex: 1,
            minHeight: 0,
            mt: 1.5,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {refreshing && (
            <LinearProgress
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 6, height: 2 }}
            />
          )}
          <MachineScheduleGrid
            bookings={visibleBookings}
            allMachineRows={visibleMachines}
            showIndexTestRows
            startDate={startDate}
            endDate={endDate}
            onCommitPlacement={handleCommitPlacement}
            onBookingClick={handleBookingClick}
            onInfo={(msg) => setToast({ message: msg })}
            onDragActiveChange={setDragActive}
            capabilityByTaskType={capabilityByTaskType}
            forceExpandAll={q.length > 0}
            showCapacity={showCapacity}
            dayColWidth={dayColWidth}
            onCreateAt={handleCreateAt}
            selectedKey={selectedBookingKey}
            conflicts={conflicts}
            fillHeight
            rowHeight={compactRows ? COMPACT_ROW_HEIGHT : ROW_HEIGHT}
            compactLabels={compactRows}
          />
        </Box>
      )}

      {/* Legend */}
      {!loading && <MachineScheduleLegend />}

      {/* Footer (Phase 4 item 9 auto-refresh sync timestamp lands in M8) */}
      <Box sx={{ pt: 1, pb: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem', display: 'block', textAlign: 'right' }}
        >
          Last synced {lastSyncedLabel}
          {refreshing ? ' · syncing…' : ''}
        </Typography>
      </Box>

      {/* Action result snackbar (with Undo) */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={toast?.message}
        action={
          toast?.onUndo ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                toast.onUndo?.();
                setToast(null);
              }}
            >
              Undo
            </Button>
          ) : undefined
        }
      />

      {/* Action error snackbar */}
      <Snackbar
        open={actionError !== null}
        autoHideDuration={5000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ width: '100%' }}>
          {actionError}
        </Alert>
      </Snackbar>

      {/* Add Event dialog */}
      <AddEventDialog
        open={addEventOpen || createEvent !== null}
        machines={machinesForManualEvents}
        defaultMachineKey={createEvent?.machineKey}
        defaultStartDate={createEvent?.start ?? startDate}
        defaultEndDate={createEvent?.end}
        onSuccess={() => {
          setAddEventOpen(false);
          setCreateEvent(null);
          void reload({ quiet: true });
          emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });
        }}
        onClose={() => {
          setAddEventOpen(false);
          setCreateEvent(null);
        }}
      />

      {/* Booking detail drawer (single-click) */}
      <BookingDetailDrawer
        open={selectedBooking !== null}
        booking={selectedBooking}
        machines={machines}
        capableMachineKeys={selectedCapableKeys}
        conflictType={selectedConflictType}
        onCommitPlacement={handleCommitPlacement}
        onRequestDelete={handleRequestDelete}
        onResolveConflict={
          conflictSuggestion && selectedBooking
            ? () =>
                // RM-23: never persist the suggestion silently — confirm first
                // (same warn-and-confirm philosophy as the drag gate; the
                // suggestion itself is now working-day + teardown aware).
                setConfirm({
                  open: true,
                  title: 'Apply suggested fix?',
                  message: `${conflictSuggestion.label} — this reschedules “${bookingPrimaryLabel(
                    selectedBooking,
                  )}”. Apply?`,
                  confirmLabel: 'Apply',
                  confirmColor: 'primary',
                  onConfirm: () =>
                    handleCommitPlacement(
                      selectedBooking,
                      conflictSuggestion.patch,
                      conflictSuggestion.label,
                    ),
                })
            : undefined
        }
        resolveLabel={conflictSuggestion?.label}
        onClose={() => setSelectedBookingKey(null)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(CONFIRM_INITIAL)} />
    </Box>
  );
};
